import type { LucideIcon } from 'lucide-react';
import {
  CalendarClock,
  Gamepad2,
  Hexagon,
  Laptop,
  ListMusic,
  LocateFixed,
  MapPin,
  Navigation,
  Plane,
  Play,
  PlayCircle,
  Route,
  Settings,
  Shield,
  Smartphone,
  Sparkles,
} from 'lucide-react';

/**
 * Mock copy for the logged-out home screen.
 * Leave `image` empty until final artwork is ready.
 * A non-empty `image` replaces the illustrated placeholder for that slot.
 */
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.mocker.mockloc';

export type Tone = 'blue' | 'green' | 'peach' | 'amber' | 'pink' | 'cyan' | 'violet';

export type ScreenVariant =
  | 'hero'
  | 'point'
  | 'playlist'
  | 'route'
  | 'joystick'
  | 'schedule'
  | 'settings'
  | 'more';

export type NavLink = { id: string; href: string; label: string };

export const NAV_LINKS: NavLink[] = [
  { id: 'top', href: '#top', label: 'Home' },
  { id: 'features', href: '#features', label: 'Features' },
  { id: 'screenshots', href: '#screenshots', label: 'Screenshots' },
  { id: 'how-it-works', href: '#how-it-works', label: 'How It Works' },
  { id: 'pricing', href: '#pricing', label: 'Pricing' },
  { id: 'help', href: '#help', label: 'Help' },
];

export const HERO_COPY = {
  badge: '#1 Mock GPS App for Android',
  titleLead: 'Mock Your Location.',
  titleAccent: 'Explore Possibilities.',
  body:
    'Easily simulate your GPS location, create custom routes, play itineraries, and more. Perfect for developers, testers, gamers and privacy-conscious users.',
};

/** Hero illustration and phone. `phone` is a full device shot, bezel included. */
export const HERO_MEDIA: { scene?: string; phone?: string } = {
  scene: '/hero-home-scene.jpg',
  phone: '/hero-home-phone.png',
};

export type Floater = {
  id: string;
  label: string;
  tone: Tone;
  Icon: LucideIcon;
};

export const FLOATERS: Floater[] = [
  { id: 'routes', label: 'Create Custom Routes', tone: 'green', Icon: Route },
  { id: 'joystick', label: 'Joystick Control', tone: 'violet', Icon: Gamepad2 },
  { id: 'schedule', label: 'Schedule Locations', tone: 'amber', Icon: CalendarClock },
  { id: 'movement', label: 'Simulate Real Movement', tone: 'pink', Icon: Navigation },
];

export type Shot = {
  id: string;
  src: string;
  alt: string;
};

/** Play Store listing order for com.mocker.mockloc. Files live in public/screenshots. */
export const SCREENSHOTS: Shot[] = Array.from({ length: 24 }, (_, index) => {
  const n = String(index + 1).padStart(2, '0');
  return {
    id: n,
    src: `/screenshots/${n}.jpg`,
    alt: `MockLoc screenshot ${index + 1}`,
  };
});

export type Feature = {
  id: string;
  title: string;
  desc: string;
  tone: Tone;
  Icon: LucideIcon;
};

export const FEATURES: Feature[] = [
  {
    id: 'point',
    title: 'Stationary Point',
    desc: 'Set a fixed location anywhere in the world.',
    tone: 'blue',
    Icon: LocateFixed,
  },
  {
    id: 'playlist',
    title: 'Route Playlist',
    desc: 'Play multiple locations as a route.',
    tone: 'green',
    Icon: ListMusic,
  },
  {
    id: 'itinerary',
    title: 'Auto Itinerary',
    desc: 'Simulate movement with realistic timing.',
    tone: 'peach',
    Icon: Route,
  },
  {
    id: 'scheduler',
    title: 'Scheduler',
    desc: 'Run locations automatically.',
    tone: 'amber',
    Icon: CalendarClock,
  },
  {
    id: 'joystick',
    title: 'Joystick Control',
    desc: 'Move freely with an on-screen joystick.',
    tone: 'pink',
    Icon: Gamepad2,
  },
  {
    id: 'polygon',
    title: 'Polygon Mover',
    desc: 'Move within a custom area or boundary.',
    tone: 'cyan',
    Icon: Hexagon,
  },
];

export type Step = {
  id: string;
  n: string;
  title: string;
  desc: string;
  tone: Tone;
  Icon: LucideIcon;
};

export const STEPS: Step[] = [
  {
    id: 'install',
    n: '1',
    title: 'Install MockLoc',
    desc: 'Download MockLoc from the Google Play Store and open it.',
    tone: 'blue',
    Icon: Play,
  },
  {
    id: 'developer',
    n: '2',
    title: 'Enable Developer Options',
    desc: 'In Settings, open About phone and tap Build number seven times.',
    tone: 'violet',
    Icon: Settings,
  },
  {
    id: 'mock-app',
    n: '3',
    title: 'Allow Mock Location',
    desc: 'Open Developer options and set MockLoc as the mock location app.',
    tone: 'pink',
    Icon: LocateFixed,
  },
  {
    id: 'permissions',
    n: '4',
    title: 'Grant Permissions',
    desc: 'Allow location and the other permissions MockLoc asks for.',
    tone: 'amber',
    Icon: Shield,
  },
  {
    id: 'start',
    n: '5',
    title: 'Start Mocking',
    desc: 'Pick a place, a playlist, or a route, then tap Start.',
    tone: 'green',
    Icon: PlayCircle,
  },
  {
    id: 'apps',
    n: '6',
    title: 'Open Your App',
    desc: 'Open Maps, a game, or any other app. It sees the mocked spot.',
    tone: 'cyan',
    Icon: Smartphone,
  },
];

export type ScenarioKind = 'testing' | 'gaming' | 'travel' | 'privacy';

export type Scenario = {
  id: string;
  title: string;
  desc: string;
  tone: Tone;
  kind: ScenarioKind;
  Icon: LucideIcon;
  slot: string;
  label: string;
  image?: string;
};

export const SCENARIOS: Scenario[] = [
  {
    id: 'testing',
    title: 'App Testing',
    desc: 'Test location-based apps without physical movement.',
    tone: 'blue',
    kind: 'testing',
    Icon: Laptop,
    slot: 'scenario-testing',
    label: 'Laptop and phone showing a map for app testing',
    image: '/scenarios/app-testing.jpg',
  },
  {
    id: 'gaming',
    title: 'Gaming',
    desc: 'Explore new locations in your favourite games.',
    tone: 'violet',
    kind: 'gaming',
    Icon: Gamepad2,
    slot: 'scenario-gaming',
    label: 'Phone game map held over a park',
    image: '/scenarios/gaming.jpg',
  },
  {
    id: 'travel',
    title: 'Travel & Planning',
    desc: 'Plan and preview trips before you go.',
    tone: 'amber',
    kind: 'travel',
    Icon: Plane,
    slot: 'scenario-travel',
    label: 'Traveler checking a map on a phone',
    image: '/scenarios/travel.jpg',
  },
  {
    id: 'privacy',
    title: 'Privacy & Security',
    desc: 'Protect your real location and stay in control.',
    tone: 'cyan',
    kind: 'privacy',
    Icon: Shield,
    slot: 'scenario-privacy',
    label: 'Lock shield over a city map',
    image: '/scenarios/privacy.jpg',
  },
];

export type Chip = { id: string; label: string; Icon: LucideIcon };

export const CHIPS: Chip[] = [
  { id: 'places', label: 'Explore Virtual Places', Icon: MapPin },
  { id: 'test', label: 'Test Location Features', Icon: LocateFixed },
  { id: 'routes', label: 'Simulate Travel Routes', Icon: Route },
  { id: 'movement', label: 'Create Realistic Movement', Icon: Navigation },
  { id: 'apps', label: 'Use with Multiple Apps', Icon: Smartphone },
  { id: 'more', label: 'And Much More', Icon: Sparkles },
];

export const POSSIBILITIES = {
  title: 'Endless Possibilities',
  lead: 'From testing to gaming to research — MockLoc opens up a world of possibilities.',
  quote: 'Your Location. Your Rules.',
};

export const PRICING = {
  title: 'Simple pricing',
  lead: 'Core MockLoc use is free. No subscription for the core app.',
  price: 'Free',
  note: 'Core use on Android and the web dashboard',
  items: [
    'Stationary points, playlists, and itineraries',
    'Joystick movement and the scheduler',
    'Web dashboard and the Android app',
    'Articles and the setup guide',
  ],
};

export const FAQS = [
  {
    q: 'Does MockLoc need a rooted phone?',
    a: 'No. Turn on Android developer options and select MockLoc as the mock location app.',
  },
  {
    q: 'Which devices work?',
    a: 'Android phones and tablets that let you choose a mock location app.',
  },
  {
    q: 'Can I manage places on the web?',
    a: 'Yes. Create a free account and use the web dashboard. The Android app uses the same account.',
  },
  {
    q: 'What can I set up?',
    a: 'Stationary points, playlists, itineraries, joystick movement, and scheduled locations.',
  },
];

export const CLOSE_COPY = {
  title: 'Ready to Explore with MockLoc?',
  lead: 'Download the app, create your first mock location and unlock new possibilities.',
  checks: ['Multiple Modes', 'Realistic Routes', 'Easy to Use'],
};

export const CLOSE_PHONES: Array<{
  slot: string;
  label: string;
  variant: ScreenVariant;
  image?: string;
}> = [
  { slot: 'close-phone-back', label: 'Route preview on a phone', variant: 'route' },
  { slot: 'close-phone-front', label: 'MockLoc home on a phone', variant: 'hero' },
];
